import { useState, useEffect } from 'react';
import TopAppBar from '../global/TopBar';
import WebsiteSideBar from '../global/WebsiteSideBar';
import HeaderPage from '../../components/Header';
import { Box, Typography, useTheme, Grid, Paper, Card, CardContent, IconButton, Button, Avatar } from '@mui/material';
import { tokens } from "../../theme";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useAuth } from "../../AuthContext";
import { useNavigate } from 'react-router-dom';

import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import FormatColorFillOutlinedIcon from '@mui/icons-material/FormatColorFillOutlined';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import WallpaperOutlinedIcon from '@mui/icons-material/WallpaperOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

const Website = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [key, setKey] = useState('websettings');
  const [websiteSettings, setWebsiteSettings] = useState(null);
  const [stats, setStats] = useState({
    totalPages: 0,
    totalBackgrounds: 0,
    totalServices: 0,
    totalProjects: 0,
    totalCsr: 0,
    totalNews: 0,
    totalEvents: 0,
    totalClients: 0,
    totalAbout: 0,
    totalDepartment: 0,
    totalModules: 0,
    totalWhy: 0,
    totalBlogs: 0,
    totalNewsLetter: 0,
    totalGallery: 0,
    totalTestimonials: 0,
    totalPartners: 0,
    totalCsrActivity: 0,
  });
  
  const { getData, user } = useAuth();
  const navigate = useNavigate();

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch website settings
  useEffect(() => {
    async function fetchWebsiteSettings() {
      try {
        const response = await getData('proccess.php', 'websettings');
        if (response.success && response.data.length > 0) {
          setWebsiteSettings(response.data[0]); // Get the first settings entry
        }
      } catch (err) {
        console.error("Error fetching website settings:", err);
      }
    }
    fetchWebsiteSettings();
  }, [getData]);

  // Fetch stats for dashboard cards
  useEffect(() => {
    async function fetchStats() {
      try {
        const endpoints = [
          { name: 'backgrounds', key: 'totalBackgrounds' },
          { name: 'services', key: 'totalServices' },
          { name: 'projects_V2', key: 'totalProjects' },
          { name: 'csr_V2', key: 'totalCsr' },
          { name: 'news_V2', key: 'totalNews' },
          { name: 'event_V2', key: 'totalEvents' },
          { name: 'our_clients_V2', key: 'totalClients' },
          { name: 'about_us', key: 'totalAbout' },
          { name: 'department', key: 'totalDepartment' },
          { name: 'modules_V2', key: 'totalModules' },
          { name: 'why_steam', key: 'totalWhy' },
          { name: 'blog', key: 'totalBlogs' },
          { name: 'newsletter_V2', key: 'totalNewsLetter' },
          { name: 'gallery', key: 'totalGallery' },
          { name: 'testmonials_V2', key: 'totalTestimonials' },
          { name: 'our_partners_V2', key: 'totalPartners' },
          { name: 'csr_events_V2', key: 'totalCsrActivity' },
        ];

        const statsData = { ...stats };
        
        for (const endpoint of endpoints) {
          try {
            const response = await getData('proccess.php', endpoint.name);
            if (response.success) {
              statsData[endpoint.key] = response.data.length;
            }
          } catch (err) {
            console.error(`Error fetching ${endpoint.name}:`, err);
          }
        }
        
        setStats(statsData);
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    }
    fetchStats();
  }, [getData]);

  // Dashboard cards data
  const dashboardCards = [
    { 
      title: 'Company Settings', 
      icon: <BusinessOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.blue[400],
      bgColor: colors.blue[200],
      count: websiteSettings ? 1 : 0,
      path: '/website/dashboard/settings',
      description: 'Manage company details'
    },
    { 
      title: 'Backgrounds', 
      icon: <WallpaperOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.green[400],
      bgColor: colors.green[200],
      count: stats.totalBackgrounds,
      path: '/website/dashboard/backgrounds',
      description: 'Manage page backgrounds'
    },
    { 
      title: 'About Us', 
      icon: <InfoOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.indigo[400],
      bgColor: colors.indigo[200],
      count: stats.totalAbout,
      path: '/website/dashboard/about-us',
      description: 'About page content'
    },
    { 
      title: 'Departments', 
      icon: <AccountTreeOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.orange[400],
      bgColor: colors.orange[200],
      count: stats.totalDepartment,
      path: '/website/dashboard/departments',
      description: 'Manage departments'
    },
    { 
      title: 'Services', 
      icon: <BuildOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.red[400],
      bgColor: colors.red[200],
      count: stats.totalServices,
      path: '/website/dashboard/services',
      description: 'Our services'
    },
    { 
      title: 'Projects', 
      icon: <AssignmentOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.blue[600],
      bgColor: colors.blue[300],
      count: stats.totalProjects,
      path: '/website/dashboard/projects',
      description: 'Company projects'
    },
    { 
      title: 'Modules', 
      icon: <ExtensionOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.green[600],
      bgColor: colors.green[300],
      count: stats.totalModules,
      path: '/website/dashboard/modules',
      description: 'System modules'
    },
    { 
      title: 'Why Steam', 
      icon: <TrendingUpOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.indigo[600],
      bgColor: colors.indigo[300],
      count: stats.totalWhy,
      path: '/website/dashboard/why-steam',
      description: 'Statistics section'
    },
    { 
      title: 'Events', 
      icon: <EventOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.orange[600],
      bgColor: colors.orange[300],
      count: stats.totalEvents,
      path: '/website/dashboard/events',
      description: 'Upcoming events'
    },
    { 
      title: 'News', 
      icon: <ArticleOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.red[600],
      bgColor: colors.red[300],
      count: stats.totalNews,
      path: '/website/dashboard/news',
      description: 'Latest news'
    },
    { 
      title: 'Our Clients', 
      icon: <GroupOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.blue[400],
      bgColor: colors.blue[200],
      count: stats.totalClients,
      path: '/website/dashboard/clients',
      description: 'Client list'
    },
    { 
      title: 'CSR Projects', 
      icon: <HandshakeOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.green[400],
      bgColor: colors.green[200],
      count: stats.totalCsr,
      path: '/website/dashboard/csr',
      description: 'CSR initiatives'
    },
    { 
      title: 'Blogs', 
      icon: <CampaignOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.indigo[400],
      bgColor: colors.indigo[200],
      count: stats.totalBlogs,
      path: '/website/dashboard/blogs',
      description: 'Blog posts'
    },
    { 
      title: 'Newsletter', 
      icon: <EmailOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.orange[400],
      bgColor: colors.orange[200],
      count: stats.totalNewsLetter,
      path: '/website/dashboard/newsletter',
      description: 'Email campaigns'
    },
    { 
      title: 'Gallery', 
      icon: <PhotoLibraryOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.red[400],
      bgColor: colors.red[200],
      count: stats.totalGallery,
      path: '/website/dashboard/gallery',
      description: 'Image gallery'
    },
    { 
      title: 'Testimonials', 
      icon: <RateReviewOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.blue[600],
      bgColor: colors.blue[300],
      count: stats.totalTestimonials,
      path: '/website/dashboard/testimonials',
      description: 'Client feedback'
    },
    { 
      title: 'Partners', 
      icon: <PeopleAltOutlinedIcon sx={{ fontSize: 40 }} />, 
      color: colors.green[600],
      bgColor: colors.green[300],
      count: stats.totalPartners,
      path: '/website/dashboard/partners',
      description: 'Partner organizations'
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Box className="w-100 flex-raw" sx={{ overflow: 'hidden' }}>
      <WebsiteSideBar />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        flex: 1,
        minWidth: 0,
        height: '100vh'
      }}>
        <TopAppBar />
        <Box 
          sx={{ 
            flex: 1,
            overflow: 'auto',
            p: 3,
            backgroundColor: colors.dark[900]
          }}
        >
          {/* Header Section */}
          <Box mb={4}>
            <HeaderPage 
              title="WEBSITE MANAGEMENT" 
              subtitle="Manage all your website content from one central dashboard"
            />
          </Box>

          {/* Welcome Section with Time */}
          <Paper 
            elevation={3}
            sx={{ 
              p: 3, 
              mb: 4,
              background: `linear-gradient(135deg, ${colors.blue[400]} 0%, ${colors.blue[600]} 100%)`,
              color: colors.white[100],
              borderRadius: 2
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h3" fontWeight="bold" mb={1} color={colors.white[500]}>
                  Welcome back, {user?.name || 'Admin'}!
                </Typography>
                <Typography variant="h6" color={colors.white[500]}>
                  Here's what's happening with your website today.
                </Typography>
              </Grid>
              <Grid item textAlign="right">
                <Typography variant="h4" fontWeight="bold" color={colors.white[500]}>
                  {formatDate(currentTime)}
                </Typography>
                <Typography variant="h5" color={colors.white[500]}>
                  {formatTime(currentTime)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Company Info Card (if settings exist) */}
          {websiteSettings && (
            <Paper 
              elevation={2}
              sx={{ 
                p: 2, 
                mb: 4,
                backgroundColor: colors.dark[800],
                border: `1px solid ${colors.dark[700]}`,
                borderRadius: 2
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={2}>
                  {websiteSettings.logo ? (
                    <Avatar 
                      src={`https://mindsinaction.com.na/assets/media/pictures/${websiteSettings.logo}`}
                      sx={{ width: 60, height: 60 }}
                      variant="rounded"
                    />
                  ) : (
                    <Avatar sx={{ width: 60, height: 60, bgcolor: colors.blue[400] }}>
                      <BusinessOutlinedIcon />
                    </Avatar>
                  )}
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {websiteSettings.site_name || 'Company Name'}
                    </Typography>
                    <Typography variant="body1" color={colors.dark[400]}>
                      {websiteSettings.short_description || 'No description available'}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => navigate('/website/dashboard/settings')}
                  sx={{ 
                    borderColor: colors.blue[400],
                    color: colors.blue[400],
                    '&:hover': {
                      borderColor: colors.blue[600],
                      backgroundColor: colors.blue[100]
                    }
                  }}
                >
                  Edit Settings
                </Button>
              </Box>
            </Paper>
          )}

          {/* Dashboard Cards Grid */}
          <Typography variant="h4" fontWeight="bold" mb={2} color={colors.dark[100]}>
            Quick Access
          </Typography>
          
          <Grid container spacing={3}>
            {dashboardCards.map((card, index) => (
              <Box key={index} sx={{
                flex: 1, minWidth: '200px'
              }}>
                <Card 
                  sx={{ 
                    backgroundColor: colors.dark[800],
                    border: `1px solid ${colors.dark[700]}`,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${colors.dark[600]}`,
                      borderColor: card.color
                    }
                  }}
                  onClick={() => handleCardClick(card.path)}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box 
                        sx={{ 
                          backgroundColor: card.bgColor,
                          borderRadius: '50%',
                          width: 60,
                          height: 60,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: card.color
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Typography variant="h3" fontWeight="bold" color={card.color}>
                        {card.count}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" mb={1}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color={colors.dark[400]}>
                      {card.description}
                    </Typography>
                    <Box mt={2} display="flex" justifyContent="flex-end">
                      <IconButton size="small" sx={{ color: card.color }}>
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Website;